/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AddContactsAIModal.tsx
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 1.0.0
 * @description Modal de 3 pasos para agregar contactos usando IA (Claude)
 * 
 * ARQUITECTURA:
 * - Step 1: INPUT - Pegar texto con información de contactos
 * - Step 2: PREVIEW - Revisar y editar contactos detectados
 * - Step 3: COMPLETE - Resumen de importación
 * 
 * CARACTERÍSTICAS:
 * ✅ 3 pasos con navegación clara
 * ✅ Análisis de texto con IA (Anthropic)
 * ✅ Preview con validación en tiempo real
 * ✅ Edición inline de contactos
 * ✅ Detección de duplicados
 * ✅ Validación de teléfonos y emails
 * ✅ React Portal (z-index 999999)
 * ✅ Estilos Bootstrap nativos
 * ✅ Dark/Light mode compatible
 * ✅ Touch-friendly
 * 
 * @date 2024-11-07
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faTimes,
  faArrowLeft,
  faArrowRight,
  faCheck,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faEdit,
  faTrash,
  faPlus
} from '@fortawesome/free-solid-svg-icons';

// Hooks
import { useAIContactExtractor, ExtractedContact } from '../../../hooks/useAIContactExtractor';
import type { ContactDB } from '../../../data/mockContactsDatabase';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface AddContactsAIModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (contacts: ContactDB[]) => void;
  checkDuplicatePhone: (phone: string) => ContactDB | undefined;
  addContact: (contact: Omit<ContactDB, 'id' | 'created_at' | 'updated_at'>) => Promise<ContactDB>;
  existingContactsCount?: number;
  existingContacts?: ContactDB[]; // ⭐ NUEVO: Para buscar en BD
}

type Step = 1 | 2 | 3;

interface EditingContact extends ExtractedContact {
  isEditing: boolean;
  shouldImport: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// ESTILOS INLINE (Bootstrap-compatible)
// ═══════════════════════════════════════════════════════════════════════════

const modalStyles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px'
  },
  modal: {
    backgroundColor: 'var(--bs-body-bg)',
    borderRadius: '8px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    maxWidth: '800px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden'
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid var(--bs-border-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  body: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto' as const
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid var(--bs-border-color)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const AddContactsAIModal: React.FC<AddContactsAIModalProps> = ({
  show,
  onHide,
  onSuccess,
  checkDuplicatePhone,
  addContact,
  existingContactsCount = 0,
  existingContacts = []
}) => {

  // ───────────────────────────────────────────────────────────────────────
  // HOOKS
  // ───────────────────────────────────────────────────────────────────────

  const {
    isExtracting,
    extractedContacts,
    searchQuery,
    intention,
    error: extractionError,
    extractContacts,
    reset: resetExtractor
  } = useAIContactExtractor();

  // ───────────────────────────────────────────────────────────────────────
  // ESTADOS
  // ───────────────────────────────────────────────────────────────────────

  const [step, setStep] = useState<Step>(1);
  const [inputText, setInputText] = useState('');
  const [editingContacts, setEditingContacts] = useState<EditingContact[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: number;
    duplicates: number;
    errors: number;
  } | null>(null);

  // ───────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Al recibir contactos extraídos, preparar para edición
   */
  useEffect(() => {
    if (extractedContacts.length > 0 && step === 1) {
      // Convertir a EditingContact
      const editing: EditingContact[] = extractedContacts.map(contact => ({
        ...contact,
        isEditing: false,
        shouldImport: contact.errors.length === 0, // Solo importar si no tiene errores
        isDuplicate: contact.phone ? !!checkDuplicatePhone(contact.phone) : false
      }));

      setEditingContacts(editing);
      setStep(2);
    }
  }, [extractedContacts, checkDuplicatePhone, step]);

  /**
   * ⭐ NUEVO: Detectar búsquedas y buscar en BD local
   */
  const searchHandledRef = useRef(false);
  
  useEffect(() => {
    if (intention === 'search' && searchQuery && !searchHandledRef.current && existingContacts.length > 0) {
      console.log('[AddContactsAIModal] Búsqueda detectada:', searchQuery);
      searchHandledRef.current = true;
      
      // Extraer términos de búsqueda del query de la IA
      // El query viene como "nombre contiene 'hector villarroel'" o similar
      // Necesitamos extraer solo los términos reales
      let searchTerms = searchQuery;
      
      // Intentar extraer texto entre comillas
      const quotedMatch = searchQuery.match(/['"]([^'"]+)['"]/);
      if (quotedMatch) {
        searchTerms = quotedMatch[1];
      } else {
        // Si no hay comillas, buscar después de "contiene", "=", etc.
        const afterContiene = searchQuery.match(/contiene\s+(.+)$/i);
        const afterEquals = searchQuery.match(/=\s+(.+)$/i);
        if (afterContiene) searchTerms = afterContiene[1].trim();
        else if (afterEquals) searchTerms = afterEquals[1].trim();
      }
      
      console.log('[AddContactsAIModal] Términos de búsqueda extraídos:', searchTerms);
      
      // Buscar en BD local
      const searchLower = searchTerms.toLowerCase();
      const results = existingContacts.filter(contact => {
        const nameMatch = contact.name?.toLowerCase().includes(searchLower);
        const phoneMatch = contact.phone?.toLowerCase().includes(searchLower);
        const emailMatch = contact.email?.toLowerCase().includes(searchLower);
        const companyMatch = contact.company?.toLowerCase().includes(searchLower);
        const positionMatch = contact.positon?.toLowerCase().includes(searchLower);
        
        return nameMatch || phoneMatch || emailMatch || companyMatch || positionMatch;
      });
      
      console.log('[AddContactsAIModal] Resultados encontrados:', results.length);
      console.log('[AddContactsAIModal] Resultados:', results.map(c => c.name));
      
      // Convertir a EditingContact para mostrar en Step 2
      const editing: EditingContact[] = results.map(contact => ({
        name: contact.name,
        phone: contact.phone,
        email: contact.email,
        company: contact.company,
        position: contact.positon,
        confidence: 1.0,
        errors: [],
        warnings: [],
        isEditing: false,
        shouldImport: false, // No permitir importar (ya están en BD)
        isDuplicate: true // Marcar como duplicados porque ya existen
      }));
      
      setEditingContacts(editing);
      setStep(2);
    }
  }, [intention, searchQuery, existingContacts]);
  
  // Resetear flag cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      searchHandledRef.current = false;
    }
  }, [show]);

  /**
   * Reset al cerrar modal
   */
  useEffect(() => {
    if (!show) {
      setTimeout(() => {
        setStep(1);
        setInputText('');
        setEditingContacts([]);
        setImportResult(null);
        resetExtractor();
      }, 300);
    }
  }, [show, resetExtractor]);

  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS - STEP 1
  // ───────────────────────────────────────────────────────────────────────

  const handleAnalyze = useCallback(async () => {
    if (!inputText.trim()) {
      return;
    }

    await extractContacts(inputText, existingContactsCount);
  }, [inputText, extractContacts, existingContactsCount]);

  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS - STEP 2
  // ───────────────────────────────────────────────────────────────────────

  const handleToggleEdit = useCallback((index: number) => {
    setEditingContacts(prev => prev.map((c, i) => 
      i === index ? { ...c, isEditing: !c.isEditing } : c
    ));
  }, []);

  const handleUpdateContact = useCallback((index: number, field: keyof ExtractedContact, value: any) => {
    setEditingContacts(prev => prev.map((c, i) => 
      i === index ? { ...c, [field]: value } : c
    ));
  }, []);

  const handleToggleImport = useCallback((index: number) => {
    setEditingContacts(prev => prev.map((c, i) => 
      i === index ? { ...c, shouldImport: !c.shouldImport } : c
    ));
  }, []);

  const handleRemoveContact = useCallback((index: number) => {
    setEditingContacts(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleImportContacts = useCallback(async () => {
    const toImport = editingContacts.filter(c => c.shouldImport && !c.isDuplicate);

    if (toImport.length === 0) {
      return;
    }

    setIsImporting(true);

    let successCount = 0;
    let errorCount = 0;

    try {
      for (const contact of toImport) {
        try {
          await addContact({
            name: contact.name,
            phone: contact.phone || '',
            email: contact.email,
            company: contact.company,
            positon: contact.position,
            status: 'active',
            groups: [],
            tags: [],
            notes: `Importado por IA con confidence ${(contact.confidence * 100).toFixed(0)}%`,
            source: 'import' // ← Valores válidos: manual | import | api | webform | oneshot
          });
          successCount++;
        } catch (err) {
          console.error('[AddContactsAIModal] Error importando contacto:', err);
          errorCount++;
        }
      }

      const duplicates = editingContacts.filter(c => c.isDuplicate).length;

      setImportResult({
        success: successCount,
        duplicates,
        errors: errorCount
      });

      setStep(3);

    } catch (err) {
      console.error('[AddContactsAIModal] Error general:', err);
    } finally {
      setIsImporting(false);
    }
  }, [editingContacts, addContact]);

  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS - STEP 3
  // ───────────────────────────────────────────────────────────────────────

  const handleComplete = useCallback(() => {
    if (importResult && importResult.success > 0) {
      // Crear array de contactos importados (mock, ya que no tenemos los IDs reales aquí)
      const importedContacts: ContactDB[] = [];
      onSuccess(importedContacts);
    }
    onHide();
  }, [importResult, onSuccess, onHide]);

  const handleAddMore = useCallback(() => {
    setStep(1);
    setInputText('');
    setEditingContacts([]);
    setImportResult(null);
    resetExtractor();
  }, [resetExtractor]);

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: STEP INDICATOR
  // ───────────────────────────────────────────────────────────────────────

  const renderStepIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: step >= s ? '#2ca799' : 'var(--bs-gray-300)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          >
            {s}
          </div>
          {s < 3 && (
            <div
              style={{
                flex: 1,
                height: '2px',
                backgroundColor: step > s ? '#2ca799' : 'var(--bs-gray-300)'
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: STEP 1 - INPUT
  // ───────────────────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <>
      <div style={modalStyles.body}>
        {renderStepIndicator()}

        <h5 style={{ fontWeight: 600, marginBottom: '8px' }}>
          Paso 1 de 3: Pegar Texto
        </h5>
        <p style={{ color: 'var(--bs-secondary-color)', fontSize: '0.875rem', marginBottom: '24px' }}>
          Pega o escribe texto con información de contactos. La IA extraerá automáticamente nombres, teléfonos, emails, empresas y cargos.
        </p>

        {extractionError && (
          <Alert variant="danger" className="mb-3">
            <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
            {extractionError}
          </Alert>
        )}

        <div className="mb-3">
          <label className="form-label" style={{ fontWeight: 500 }}>
            Pega aquí tu texto con información de contactos:
          </label>
          <textarea
            className="form-control"
            rows={10}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Ejemplo:&#10;&#10;Juan Pérez, gerente de ventas en Acme Corp, teléfono +56912345678, juan@acme.cl&#10;&#10;María González, directora de marketing en Tech Solutions, correo maria@tech.cl, fono 912345679"
            disabled={isExtracting}
            style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
          />
          <small className="text-muted">
            {inputText.length} caracteres
          </small>
        </div>

        <div 
          style={{
            backgroundColor: 'rgba(44, 167, 153, 0.1)',
            border: '1px solid #2ca799',
            borderRadius: '6px',
            padding: '16px',
            marginTop: '20px'
          }}
        >
          <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '0.875rem', color: '#2ca799' }}>
            💡 La IA puede extraer:
          </h6>
          <ul style={{ fontSize: '0.813rem', marginBottom: 0, paddingLeft: '20px' }}>
            <li>Nombres completos</li>
            <li>Teléfonos (formato chileno +56XXXXXXXXX)</li>
            <li>Emails</li>
            <li>Empresas</li>
            <li>Cargos o posiciones</li>
          </ul>
        </div>
      </div>

      <div style={modalStyles.footer}>
        <button
          className="btn btn-secondary"
          onClick={onHide}
          disabled={isExtracting}
        >
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          onClick={handleAnalyze}
          disabled={!inputText.trim() || isExtracting}
        >
          {isExtracting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Analizando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faRobot} className="me-2" />
              Analizar con IA
            </>
          )}
        </button>
      </div>
    </>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: STEP 2 - PREVIEW
  // ───────────────────────────────────────────────────────────────────────

  const renderContactCard = (contact: EditingContact, index: number) => {
    const hasErrors = contact.errors.length > 0;
    const hasWarnings = contact.warnings.length > 0;

    return (
      <div
        key={index}
        style={{
          border: '1px solid var(--bs-border-color)',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '12px',
          backgroundColor: contact.shouldImport 
            ? 'var(--bs-body-bg)' 
            : 'var(--bs-secondary-bg)',
          opacity: contact.shouldImport ? 1 : 0.6
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={contact.shouldImport}
                onChange={() => handleToggleImport(index)}
                style={{ marginTop: 0 }}
              />
              <strong style={{ fontSize: '1rem' }}>
                {index + 1}. {contact.name}
              </strong>
              {!hasErrors && (
                <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                  Válido
                </span>
              )}
              {hasErrors && (
                <span className="badge bg-danger" style={{ fontSize: '0.75rem' }}>
                  <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                  Error
                </span>
              )}
              {contact.isDuplicate && (
                <span className="badge bg-warning text-dark" style={{ fontSize: '0.75rem' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                  Duplicado
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
              Confidence: {(contact.confidence * 100).toFixed(0)}%
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleToggleEdit(index)}
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleRemoveContact(index)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>

        {/* Campos */}
        {contact.isEditing ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            <div>
              <label className="form-label" style={{ fontSize: '0.813rem', marginBottom: '4px' }}>
                Nombre *
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={contact.name}
                onChange={(e) => handleUpdateContact(index, 'name', e.target.value)}
              />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.813rem', marginBottom: '4px' }}>
                Teléfono
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={contact.phone || ''}
                onChange={(e) => handleUpdateContact(index, 'phone', e.target.value)}
              />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.813rem', marginBottom: '4px' }}>
                Email
              </label>
              <input
                type="email"
                className="form-control form-control-sm"
                value={contact.email || ''}
                onChange={(e) => handleUpdateContact(index, 'email', e.target.value)}
              />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.813rem', marginBottom: '4px' }}>
                Empresa
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={contact.company || ''}
                onChange={(e) => handleUpdateContact(index, 'company', e.target.value)}
              />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.813rem', marginBottom: '4px' }}>
                Cargo
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={contact.position || ''}
                onChange={(e) => handleUpdateContact(index, 'position', e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.875rem' }}>
            {contact.phone && (
              <div style={{ marginBottom: '8px' }}>
                📞 {contact.phone}
                {contact.phone && /^\+56[9]\d{8}$/.test(contact.phone) && (
                  <span className="badge bg-success ms-2" style={{ fontSize: '0.7rem' }}>✓</span>
                )}
              </div>
            )}
            {contact.email && (
              <div style={{ marginBottom: '8px' }}>
                📧 {contact.email}
                {contact.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email) && (
                  <span className="badge bg-success ms-2" style={{ fontSize: '0.7rem' }}>✓</span>
                )}
              </div>
            )}
            {contact.company && (
              <div style={{ marginBottom: '8px' }}>
                🏢 {contact.company}
              </div>
            )}
            {contact.position && (
              <div style={{ marginBottom: '8px' }}>
                💼 {contact.position}
              </div>
            )}
          </div>
        )}

        {/* Errores y warnings */}
        {hasErrors && (
          <Alert variant="danger" className="mt-2 mb-0" style={{ fontSize: '0.813rem', padding: '8px' }}>
            {contact.errors.map((err, i) => (
              <div key={i}>• {err}</div>
            ))}
          </Alert>
        )}
        {hasWarnings && !hasErrors && (
          <Alert variant="warning" className="mt-2 mb-0" style={{ fontSize: '0.813rem', padding: '8px' }}>
            {contact.warnings.map((warn, i) => (
              <div key={i}>• {warn}</div>
            ))}
          </Alert>
        )}
        {contact.isDuplicate && (
          <Alert variant="warning" className="mt-2 mb-0" style={{ fontSize: '0.813rem', padding: '8px' }}>
            ⚠️ Este número de teléfono ya existe en la base de datos
          </Alert>
        )}
      </div>
    );
  };

  const renderStep2 = () => {
    const validCount = editingContacts.filter(c => c.shouldImport && !c.isDuplicate).length;
    const isSearchMode = intention === 'search';

    return (
      <>
        <div style={modalStyles.body}>
          {renderStepIndicator()}

          <h5 style={{ fontWeight: 600, marginBottom: '8px' }}>
            {isSearchMode ? '🔍 Resultados de Búsqueda' : 'Paso 2 de 3: Revisar Contactos'}
          </h5>
          <p style={{ color: 'var(--bs-secondary-color)', fontSize: '0.875rem', marginBottom: '16px' }}>
            {isSearchMode 
              ? `Encontrados ${editingContacts.length} contactos que coinciden con: "${searchQuery}"`
              : 'Revisa los contactos detectados. Puedes editarlos o eliminarlos antes de importar.'
            }
          </p>

          {editingContacts.length === 0 ? (
            <Alert variant="warning">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              {isSearchMode 
                ? 'No se encontraron contactos que coincidan con tu búsqueda'
                : 'No se detectaron contactos válidos en el texto'
              }
            </Alert>
          ) : (
            <>
              <div 
                style={{
                  backgroundColor: 'rgba(44, 167, 153, 0.1)',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ fontWeight: 600, color: '#2ca799' }}>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  {validCount} contactos válidos listos para importar
                </div>
                {editingContacts.filter(c => c.isDuplicate).length > 0 && (
                  <div style={{ fontSize: '0.875rem', color: 'var(--bs-warning)' }}>
                    {editingContacts.filter(c => c.isDuplicate).length} duplicados omitidos
                  </div>
                )}
              </div>

              {editingContacts.map((contact, index) => renderContactCard(contact, index))}
            </>
          )}
        </div>

        <div style={modalStyles.footer}>
          <button
            className="btn btn-secondary"
            onClick={() => setStep(1)}
            disabled={isImporting}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            {isSearchMode ? 'Nueva Búsqueda' : 'Volver'}
          </button>
          {!isSearchMode && (
            <button
              className="btn btn-primary"
              onClick={handleImportContacts}
              disabled={validCount === 0 || isImporting}
            >
              {isImporting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Importando...
                </>
              ) : (
                <>
                  Guardar Contactos ({validCount})
                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </>
              )}
            </button>
          )}
          {isSearchMode && (
            <button
              className="btn btn-primary"
              onClick={onHide}
            >
              Cerrar
            </button>
          )}
        </div>
      </>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: STEP 3 - COMPLETE
  // ───────────────────────────────────────────────────────────────────────

  const renderStep3 = () => (
    <>
      <div style={modalStyles.body}>
        {renderStepIndicator()}

        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{ fontSize: '4rem', marginBottom: '24px' }}>
            {importResult && importResult.success > 0 ? '🎉' : '⚠️'}
          </div>

          <h4 style={{ fontWeight: 600, marginBottom: '16px' }}>
            {importResult && importResult.success > 0 
              ? '¡Importación Completada!' 
              : 'Importación con Advertencias'}
          </h4>

          {importResult && (
            <div style={{ marginTop: '32px', display: 'grid', gap: '16px', maxWidth: '400px', margin: '32px auto 0' }}>
              {importResult.success > 0 && (
                <div 
                  style={{
                    padding: '16px',
                    backgroundColor: 'rgba(25, 135, 84, 0.1)',
                    border: '1px solid var(--bs-success)',
                    borderRadius: '6px'
                  }}
                >
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--bs-success)' }}>
                    {importResult.success}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--bs-success)' }}>
                    Contactos agregados correctamente
                  </div>
                </div>
              )}

              {importResult.duplicates > 0 && (
                <div 
                  style={{
                    padding: '16px',
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid var(--bs-warning)',
                    borderRadius: '6px'
                  }}
                >
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--bs-warning)' }}>
                    {importResult.duplicates}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--bs-warning)' }}>
                    Duplicados omitidos
                  </div>
                </div>
              )}

              {importResult.errors > 0 && (
                <div 
                  style={{
                    padding: '16px',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    border: '1px solid var(--bs-danger)',
                    borderRadius: '6px'
                  }}
                >
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--bs-danger)' }}>
                    {importResult.errors}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--bs-danger)' }}>
                    Errores al importar
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={modalStyles.footer}>
        <button
          className="btn btn-secondary"
          onClick={handleAddMore}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Agregar Más Contactos
        </button>
        <button
          className="btn btn-primary"
          onClick={handleComplete}
        >
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          Cerrar
        </button>
      </div>
    </>
  );

  // ───────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ───────────────────────────────────────────────────────────────────────

  if (!show) return null;

  const modalContent = (
    <div style={modalStyles.overlay} onClick={onHide}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.header}>
          <div>
            <h5 style={{ margin: 0, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FontAwesomeIcon icon={faRobot} style={{ color: '#2ca799' }} />
              Agregar Contactos con IA
            </h5>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--bs-secondary-color)' }}>
              Extrae contactos desde texto usando inteligencia artificial
            </p>
          </div>
          <button
            className="btn-close"
            onClick={onHide}
            disabled={isExtracting || isImporting}
            aria-label="Cerrar"
          />
        </div>

        {/* Body & Footer (según step) */}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddContactsAIModal;